// src/components/auth/CentralUnavailableOverlay.tsx
// Non-dismissible overlay when Central Identity cannot be reached.
// No close button, no Escape, no click-outside dismiss.
// No fallback to cached context. No partial rendering. No read-only mode.

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppSelector } from '../../store/store';
import { selectBootError } from '../../store/slices/bootContextSlice';
import { useBootGate } from './BootGate';
import LoadingSpinner from '../common/LoadingSpinner';

export default function CentralUnavailableOverlay() {
  const bootError = useAppSelector(selectBootError);
  const { rerunBoot } = useBootGate();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    rerunBoot();
    // The retry result is handled by BootGate; give a brief delay before re-enabling button
    setTimeout(() => setIsRetrying(false), 2000);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/80 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-label="Central Identity unavailable"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728M5.636 5.636a9 9 0 000 12.728M12 9v4m0 4h.01" />
          </svg>
        </div>

        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Unable to Connect to Central Identity
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          The application cannot verify your session. This may be a temporary issue.
        </p>

        {bootError && import.meta.env.DEV && (
          <p className="text-xs text-gray-400 mb-4 font-mono bg-gray-50 rounded px-3 py-2 break-all">
            {bootError}
          </p>
        )}

        <button
          onClick={handleRetry}
          disabled={isRetrying}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRetrying ? (
            <>
              <LoadingSpinner size="sm" />
              Retrying...
            </>
          ) : (
            'Retry Connection'
          )}
        </button>
      </div>
    </div>,
    document.body
  );
}
